import { AxiosPromise } from "axios";
import { ApiRequestAdapter } from "../../../common/api/ApiRequestAdapter";
import { apiService } from "../../../common/api/apiService";
import { HttpContentType, HttpHeaders, HttpMethod } from "../../../common/api/http";
import {
  CreateUpdateEntries,
  EntityIdObject,
  EntityObject,
  OptimisticLockVersion,
  TwoLevelEntityIdObject,
  TwoLevelEntityObject,
} from "../../../common/types";
import {
  AssignCommissionsSettingsLevel,
  CommissionsSettings,
  CommissionsSettingsAgentProfile,
  CommissionsSettingsAttachment,
  CommissionsSettingsRankInterval,
  CommissionsSettingsRule,
  CreateCommissionsSettingsRule,
  CreateUpdateCommissionsSettingsRankInterval,
  UpdateCommissionsSettingsInitialPoints,
  UpdateCommissionsSettingsRule,
  UpdateCommissionsSettingsStatus,
} from "./types";

const GET_COMMISSIONS_SETTINGS = ApiRequestAdapter.create("/agents/{0}/commissions-settings", HttpMethod.GET);
const GET_PROFILE_COMMISSIONS_SETTINGS = ApiRequestAdapter.create(
  "/agents/{0}/commissions-settings/profile",
  HttpMethod.GET,
);

const ASSIGN_COMMISSIONS_SETTINGS_LEVEL = ApiRequestAdapter.create(
  "/agents/{0}/commissions-settings/level/assign",
  HttpMethod.PUT,
);

const UNASSIGN_COMMISSIONS_SETTINGS_LEVEL = ApiRequestAdapter.create(
  "/agents/{0}/commissions-settings/level/unassign",
  HttpMethod.PUT,
);

const GET_AGENT_COMMISSIONS_SETTINGS_RANK_INTERVALS = ApiRequestAdapter.create(
  "/agents/{0}/commissions-settings/ranks",
  HttpMethod.GET,
);

const UPDATE_AGENT_COMMISSIONS_SETTINGS_RANK_INTERVALS = ApiRequestAdapter.create(
  "/agents/{0}/commissions-settings/ranks",
  HttpMethod.PUT,
);

const TOGGLE_PRIMARY_PAYMENT_INCLUSION_COMMISSIONS_SETTINGS = ApiRequestAdapter.create(
  "/agents/{0}/commissions-settings/primary-payment-inclusion",
  HttpMethod.PUT,
);

const TOGGLE_SECONDARY_PAYMENT_INCLUSION_COMMISSIONS_SETTINGS = ApiRequestAdapter.create(
  "/agents/{0}/commissions-settings/secondary-payment-inclusion",
  HttpMethod.PUT,
);

const TOGGLE_USAGE_AS_HEAD_COMMISSIONS_SETTINGS = ApiRequestAdapter.create(
  "/agents/{0}/commissions-settings/usage-as-head",
  HttpMethod.PUT,
);

const TOGGLE_POINTS_UNIFICATION_COMMISSIONS_SETTINGS = ApiRequestAdapter.create(
  "/agents/{0}/commissions-settings/points-unification",
  HttpMethod.PUT,
);

const TOGGLE_PRIMARY_MANAGERIAL_COMMISSIONS = ApiRequestAdapter.create(
  "/agents/{0}/commissions-settings/primary-managerial-commissions",
  HttpMethod.PUT,
);

const TOGGLE_SECONDARY_MANAGERIAL_COMMISSIONS = ApiRequestAdapter.create(
  "/agents/{0}/commissions-settings/secondary-managerial-commissions",
  HttpMethod.PUT,
);

const TOGGLE_PROMOTION_ALLOWANCE = ApiRequestAdapter.create(
  "/agents/{0}/commissions-settings/promotion-allowance",
  HttpMethod.PUT,
);

const UPDATE_INITIAL_POINTS = ApiRequestAdapter.create(
  "/agents/{0}/commissions-settings/initial-points",
  HttpMethod.PUT,
);

const UPDATE_STATUS_COMMISSIONS_SETTINGS = ApiRequestAdapter.create(
  "/agents/{0}/commissions-settings/status",
  HttpMethod.PUT,
);

const CREATE_COMMISSIONS_SETTINGS_RULE = ApiRequestAdapter.create(
  "/agents/{0}/commissions-settings/rules",
  HttpMethod.POST,
);

const UPDATE_COMMISSIONS_SETTINGS_RULE = ApiRequestAdapter.create(
  "/agents/{0}/commissions-settings/rules/{1}",
  HttpMethod.PUT,
);

const DELETE_COMMISSIONS_SETTINGS_RULE = ApiRequestAdapter.create(
  "/agents/{0}/commissions-settings/rules/{1}",
  HttpMethod.DELETE,
);

const UPLOAD_COMMISSIONS_SETTINGS_ATTACHMENTS = ApiRequestAdapter.create(
  "/agents/{0}/commissions-settings/attachments",
  HttpMethod.POST,
);

const DOWNLOAD_COMMISSIONS_SETTINGS_ATTACHMENT = ApiRequestAdapter.create(
  "/agents/{0}/commissions-settings/attachments/{1}",
  HttpMethod.GET,
);

const DELETE_COMMISSIONS_SETTINGS_ATTACHMENT = ApiRequestAdapter.create(
  "/agents/{0}/commissions-settings/attachments/{1}",
  HttpMethod.DELETE,
);

export const commissionsSettingsApi = {
  getCommissionsSettings: (request: EntityIdObject): AxiosPromise<CommissionsSettings> => {
    return apiService.get(GET_COMMISSIONS_SETTINGS.getParametrizedUrl(request.id));
  },
  getProfileCommissionsSettings: (request: EntityIdObject): AxiosPromise<CommissionsSettingsAgentProfile> => {
    return apiService.get(GET_PROFILE_COMMISSIONS_SETTINGS.getParametrizedUrl(request.id));
  },
  assignCommissionsSettingsLevel: (
    request: EntityObject<AssignCommissionsSettingsLevel>,
  ): AxiosPromise<CommissionsSettings> => {
    return apiService.put(ASSIGN_COMMISSIONS_SETTINGS_LEVEL.getParametrizedUrl(request.id), request.object);
  },
  unassignCommissionsSettingsLevel: (
    request: EntityObject<OptimisticLockVersion>,
  ): AxiosPromise<CommissionsSettings> => {
    return apiService.put(UNASSIGN_COMMISSIONS_SETTINGS_LEVEL.getParametrizedUrl(request.id), request.object);
  },
  getAgentCommissionsSettingsRankIntervals: (
    request: EntityIdObject,
  ): AxiosPromise<CommissionsSettingsRankInterval[]> => {
    return apiService.get(GET_AGENT_COMMISSIONS_SETTINGS_RANK_INTERVALS.getParametrizedUrl(request.id));
  },
  updateAgentCommissionsSettingsRankIntervals: (
    request: EntityObject<CreateUpdateEntries<CreateUpdateCommissionsSettingsRankInterval>>,
  ): AxiosPromise<CommissionsSettings> => {
    return apiService.put(
      UPDATE_AGENT_COMMISSIONS_SETTINGS_RANK_INTERVALS.getParametrizedUrl(request.id),
      request.object,
    );
  },
  togglePrimaryPaymentInclusionCommissionsSettings: (
    request: EntityObject<OptimisticLockVersion>,
  ): AxiosPromise<CommissionsSettings> => {
    return apiService.put(
      TOGGLE_PRIMARY_PAYMENT_INCLUSION_COMMISSIONS_SETTINGS.getParametrizedUrl(request.id),
      request.object,
    );
  },
  toggleSecondaryPaymentInclusionCommissionsSettings: (
    request: EntityObject<OptimisticLockVersion>,
  ): AxiosPromise<CommissionsSettings> => {
    return apiService.put(
      TOGGLE_SECONDARY_PAYMENT_INCLUSION_COMMISSIONS_SETTINGS.getParametrizedUrl(request.id),
      request.object,
    );
  },
  toggleUsageAsHeadCommissionsSettings: (
    request: EntityObject<OptimisticLockVersion>,
  ): AxiosPromise<CommissionsSettings> => {
    return apiService.put(TOGGLE_USAGE_AS_HEAD_COMMISSIONS_SETTINGS.getParametrizedUrl(request.id), request.object);
  },
  togglePointsUnificationCommissionsSettings: (
    request: EntityObject<OptimisticLockVersion>,
  ): AxiosPromise<CommissionsSettings> => {
    return apiService.put(
      TOGGLE_POINTS_UNIFICATION_COMMISSIONS_SETTINGS.getParametrizedUrl(request.id),
      request.object,
    );
  },
  togglePrimaryManagerialCommissions: (
    request: EntityObject<OptimisticLockVersion>,
  ): AxiosPromise<CommissionsSettings> => {
    return apiService.put(TOGGLE_PRIMARY_MANAGERIAL_COMMISSIONS.getParametrizedUrl(request.id), request.object);
  },
  toggleSecondaryManagerialCommissions: (
    request: EntityObject<OptimisticLockVersion>,
  ): AxiosPromise<CommissionsSettings> => {
    return apiService.put(TOGGLE_SECONDARY_MANAGERIAL_COMMISSIONS.getParametrizedUrl(request.id), request.object);
  },
  togglePromotionAllowance: (request: EntityObject<OptimisticLockVersion>): AxiosPromise<CommissionsSettings> => {
    return apiService.put(TOGGLE_PROMOTION_ALLOWANCE.getParametrizedUrl(request.id), request.object);
  },
  updateInitialPoints: (
    request: EntityObject<UpdateCommissionsSettingsInitialPoints>,
  ): AxiosPromise<CommissionsSettings> => {
    return apiService.put(UPDATE_INITIAL_POINTS.getParametrizedUrl(request.id), request.object);
  },
  updateStatusCommissionsSettings: (
    request: EntityObject<UpdateCommissionsSettingsStatus>,
  ): AxiosPromise<CommissionsSettings> => {
    return apiService.put(UPDATE_STATUS_COMMISSIONS_SETTINGS.getParametrizedUrl(request.id), request.object);
  },
  createCommissionsSettingsRule: (
    request: EntityObject<CreateCommissionsSettingsRule>,
  ): AxiosPromise<CommissionsSettingsRule> => {
    return apiService.post(CREATE_COMMISSIONS_SETTINGS_RULE.getParametrizedUrl(request.id), request.object);
  },
  updateCommissionsSettingsRule: (
    request: TwoLevelEntityObject<UpdateCommissionsSettingsRule>,
  ): AxiosPromise<CommissionsSettingsRule> => {
    return apiService.put(
      UPDATE_COMMISSIONS_SETTINGS_RULE.getParametrizedUrl(request.id1, request.id2),
      request.object,
    );
  },
  deleteCommissionsSettingsRule: (request: TwoLevelEntityIdObject): AxiosPromise => {
    return apiService.delete(DELETE_COMMISSIONS_SETTINGS_RULE.getParametrizedUrl(request.id1, request.id2));
  },
  uploadCommissionsSettingsAttachments: (
    request: EntityObject<FormData>,
  ): AxiosPromise<CommissionsSettingsAttachment[]> => {
    return apiService.post(UPLOAD_COMMISSIONS_SETTINGS_ATTACHMENTS.getParametrizedUrl(request.id), request.object, {
      headers: { [HttpHeaders.CONTENT_TYPE]: HttpContentType.MULTIPART_FORM_DATA },
    });
  },
  downloadCommissionsSettingsAttachment: (request: TwoLevelEntityIdObject): AxiosPromise<Blob> => {
    return apiService.get(DOWNLOAD_COMMISSIONS_SETTINGS_ATTACHMENT.getParametrizedUrl(request.id1, request.id2), {
      responseType: "blob",
    });
  },
  deleteCommissionsSettingsAttachment: (request: TwoLevelEntityIdObject): AxiosPromise => {
    return apiService.delete(DELETE_COMMISSIONS_SETTINGS_ATTACHMENT.getParametrizedUrl(request.id1, request.id2));
  },
};
